import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  LogIn,
  ChevronRight,
  Monitor,
  Rocket,
  Sparkles,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import logoImage from "@/assets/images/logo.png";

const serviceNavigation = [
  { name: "View Services", href: "#services-section", icon: Sparkles, scroll: true },
  { name: "Desktop Packages", href: "#pricing", icon: Monitor, scroll: true },
  { name: "AI App Launch", href: "#pricing", icon: Rocket, scroll: true },
];

const companyNavigation = [
  { name: "About", href: "/about", icon: User },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handlePortalClick = () => {
    navigate("/login");
  };

  const handleViewServices = () => {
    navigate("/services");
  };

  const scrollToPricing = () => {
    if (location.pathname === "/") {
      document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#pricing");
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50" role="banner">
      <div className="absolute inset-0 bg-background/85 backdrop-blur-xl border-b border-white/5" />

      <nav
        className="relative container mx-auto px-6"
        aria-label="Primary navigation"
      >
        <div className="flex items-center justify-between gap-4 py-4">
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <img src={logoImage} alt="Echopeak Labs" className="h-10" />
            <span
              className="text-xl font-bold leading-tight hidden sm:inline"
              style={{
                background: "linear-gradient(to right, #fa9710, #feca09)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                filter: "drop-shadow(0px 0px 30px #fa9710)",
              }}
            >
              Echopeak Labs
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5 min-w-0">
            <Link
              to="/about"
              className={cn(
                "text-sm whitespace-nowrap transition-colors",
                location.pathname === "/about"
                  ? "text-white"
                  : "text-muted-foreground hover:text-white",
              )}
            >
              About
            </Link>

            <button
              type="button"
              onClick={handleViewServices}
              className="text-sm text-muted-foreground hover:text-white transition-colors whitespace-nowrap"
            >
              Services
            </button>

            <button
              type="button"
              onClick={scrollToPricing}
              className="text-sm text-muted-foreground hover:text-white transition-colors whitespace-nowrap"
            >
              Packages
            </button>

            <div className="h-5 w-px bg-white/10 shrink-0" />

            <button
              onClick={handlePortalClick}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-white transition-colors whitespace-nowrap group"
              aria-label="Access client portal"
            >
              Client Portal
              <ChevronRight className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>

          <div className="lg:hidden">
            <button
              type="button"
              className="p-2 -mr-2 text-muted-foreground hover:text-white"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-80 bg-card border-l border-white/10 z-50 lg:hidden flex flex-col"
            >
              <div className="flex items-center justify-end p-4 border-b border-white/5">
                <button
                  type="button"
                  className="p-2 text-muted-foreground hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest px-3 mb-2">
                    Services
                  </p>
                  {serviceNavigation.map((item, index) => {
                    const Icon = item.icon;
                    const isScroll = "scroll" in item && item.scroll;

                    if (isScroll) {
                      return (
                        <motion.button
                          key={item.name}
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          type="button"
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            if (item.href === "#pricing") {
                              scrollToPricing();
                            } else {
                              handleViewServices();
                            }
                          }}
                        >
                          <Icon className="h-4 w-4 text-primary/70" />
                          {item.name}
                        </motion.button>
                      );
                    }

                    return null;
                  })}
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest px-3 mb-2">
                    Company
                  </p>
                  {companyNavigation.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: serviceNavigation.length * 0.05 + index * 0.05,
                        }}
                      >
                        <Link
                          to={item.href}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                            isActive
                              ? "text-primary bg-primary/10"
                              : "text-muted-foreground hover:text-white hover:bg-white/5",
                          )}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <Icon
                            className={cn(
                              "h-4 w-4",
                              isActive ? "text-primary" : "text-primary/70",
                            )}
                          />
                          {item.name}
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              <div className="p-5 border-t border-white/10 space-y-3 bg-background/50">
                <button
                  type="button"
                  className="w-full text-sm font-medium text-foreground hover:text-primary transition-colors"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    scrollToPricing();
                  }}
                >
                  See Packages
                </button>

                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground hover:text-white transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LogIn className="h-4 w-4" />
                  Client Portal
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
